import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {
    ProfessionalPortfolio,
    ProfessionalPortfolioItem,
    ProfessionalProfile,
    ProfessionalProfileRecord,
    ProfessionalRatingSummary,
    ProfessionalSkill
} from "../../utils/types";
import ProfileService from "./profile_services";
import {
    ProfileState,
    UpdatePortfolioPayload,
    UpdateProfessionalProfilePayload,
    UpsertPortfolioItemPayload,
    UpsertSkillPayload
} from "./types";

const initialState: ProfileState = {
    publicProfile: null,
    portfolioItems: [],
    skills: [],
    ratingSummary: null,
    loading: false,
    error: null,
};

export const fetchPublicProfile = createAsyncThunk(
    "profile/fetchPublicProfile",
    async (username: string, {rejectWithValue}): Promise<ProfessionalProfile | any> => {
        try {
            return await ProfileService.getPublicProfile(username);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchMyProfile = createAsyncThunk(
    "profile/fetchMyProfile",
    async (_: void, {rejectWithValue}): Promise<ProfessionalProfile | any> => {
        try {
            return await ProfileService.getMyProfile();
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchProfilePortfolioItems = createAsyncThunk(
    "profile/fetchProfilePortfolioItems",
    async (username: string, {rejectWithValue}): Promise<ProfessionalPortfolioItem[] | any> => {
        try {
            return await ProfileService.getPortfolioItems(username);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchProfileSkills = createAsyncThunk(
    "profile/fetchProfileSkills",
    async (username: string, {rejectWithValue}): Promise<ProfessionalSkill[] | any> => {
        try {
            return await ProfileService.getSkills(username);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchProfileRatingSummary = createAsyncThunk(
    "profile/fetchProfileRatingSummary",
    async (username: string, {rejectWithValue}): Promise<ProfessionalRatingSummary | null | any> => {
        try {
            return await ProfileService.getRatingSummary(username);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const updateMyProfessionalProfile = createAsyncThunk(
    "profile/updateMyProfessionalProfile",
    async (payload: UpdateProfessionalProfilePayload, {rejectWithValue}): Promise<ProfessionalProfileRecord | any> => {
        try {
            return await ProfileService.updateMyProfile(payload);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const updateMyProfessionalPortfolio = createAsyncThunk(
    "profile/updateMyProfessionalPortfolio",
    async (payload: UpdatePortfolioPayload, {rejectWithValue}): Promise<ProfessionalPortfolio | any> => {
        try {
            return await ProfileService.updateMyPortfolio(payload);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const createMyPortfolioItem = createAsyncThunk(
    "profile/createMyPortfolioItem",
    async (payload: UpsertPortfolioItemPayload, {rejectWithValue}): Promise<ProfessionalPortfolioItem | any> => {
        try {
            return await ProfileService.createPortfolioItem(payload);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const updateMyPortfolioItem = createAsyncThunk(
    "profile/updateMyPortfolioItem",
    async (
        {itemId, payload}: {itemId: string | number; payload: UpsertPortfolioItemPayload},
        {rejectWithValue}
    ): Promise<ProfessionalPortfolioItem | any> => {
        try {
            return await ProfileService.updatePortfolioItem(itemId, payload);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const deleteMyPortfolioItem = createAsyncThunk(
    "profile/deleteMyPortfolioItem",
    async (itemId: string | number, {rejectWithValue}): Promise<string | number | any> => {
        try {
            await ProfileService.deletePortfolioItem(itemId);
            return itemId;
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const createMySkill = createAsyncThunk(
    "profile/createMySkill",
    async (payload: UpsertSkillPayload, {rejectWithValue}): Promise<ProfessionalSkill | any> => {
        try {
            return await ProfileService.createSkill(payload);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const updateMySkill = createAsyncThunk(
    "profile/updateMySkill",
    async (
        {skillId, payload}: {skillId: string | number; payload: UpsertSkillPayload},
        {rejectWithValue}
    ): Promise<ProfessionalSkill | any> => {
        try {
            return await ProfileService.updateSkill(skillId, payload);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const deleteMySkill = createAsyncThunk(
    "profile/deleteMySkill",
    async (skillId: string | number, {rejectWithValue}): Promise<string | number | any> => {
        try {
            await ProfileService.deleteSkill(skillId);
            return skillId;
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

const setPending = (state: ProfileState) => {
    state.loading = true;
    state.error = null;
};

const setRejected = (state: ProfileState, action: any) => {
    state.loading = false;
    state.error = action.payload || action.error?.message || 'Something went wrong';
};

const profileSlice = createSlice({
    name: "profile",
    initialState,
    reducers: {
        clearProfileState: (state) => {
            state.publicProfile = null;
            state.portfolioItems = [];
            state.skills = [];
            state.ratingSummary = null;
            state.loading = false;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchMyProfile.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.publicProfile = null;
                state.portfolioItems = [];
                state.skills = [];
                state.ratingSummary = null;
            })
            .addCase(fetchMyProfile.fulfilled, (state, action) => {
                state.loading = false;
                state.publicProfile = action.payload || null;
                state.skills = action.payload?.skills || [];
                state.ratingSummary = action.payload?.rating_summary || null;
                state.portfolioItems = action.payload?.portfolio_items || action.payload?.featured_portfolio_items || [];
            })
            .addCase(fetchMyProfile.rejected, setRejected)
            .addCase(fetchPublicProfile.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.publicProfile = null;
                state.portfolioItems = [];
                state.skills = [];
                state.ratingSummary = null;
            })
            .addCase(fetchPublicProfile.fulfilled, (state, action) => {
                state.loading = false;
                state.publicProfile = action.payload || null;
                state.skills = action.payload?.skills || state.skills;
                state.ratingSummary = action.payload?.rating_summary || state.ratingSummary;
                state.portfolioItems = action.payload?.featured_portfolio_items || state.portfolioItems;
            })
            .addCase(fetchPublicProfile.rejected, setRejected)
            .addCase(fetchProfilePortfolioItems.pending, setPending)
            .addCase(fetchProfilePortfolioItems.fulfilled, (state, action) => {
                state.loading = false;
                const nextItems = action.payload || [];
                if (Array.isArray(nextItems) && nextItems.length === 0 && state.portfolioItems.length > 0) {
                    return;
                }
                state.portfolioItems = nextItems;
            })
            .addCase(fetchProfilePortfolioItems.rejected, setRejected)
            .addCase(fetchProfileSkills.pending, setPending)
            .addCase(fetchProfileSkills.fulfilled, (state, action) => {
                state.loading = false;
                state.skills = action.payload || [];
            })
            .addCase(fetchProfileSkills.rejected, setRejected)
            .addCase(fetchProfileRatingSummary.pending, setPending)
            .addCase(fetchProfileRatingSummary.fulfilled, (state, action) => {
                state.loading = false;
                state.ratingSummary = action.payload || null;
            })
            .addCase(fetchProfileRatingSummary.rejected, setRejected)
            .addCase(updateMyProfessionalProfile.pending, setPending)
            .addCase(updateMyProfessionalProfile.fulfilled, (state, action) => {
                state.loading = false;
                if (state.publicProfile) {
                    state.publicProfile = {
                        ...state.publicProfile,
                        profile: action.payload,
                    };
                }
            })
            .addCase(updateMyProfessionalProfile.rejected, setRejected)
            .addCase(updateMyProfessionalPortfolio.pending, setPending)
            .addCase(updateMyProfessionalPortfolio.fulfilled, (state, action) => {
                state.loading = false;
                if (state.publicProfile) {
                    state.publicProfile = {
                        ...state.publicProfile,
                        portfolio: action.payload,
                    };
                }
            })
            .addCase(updateMyProfessionalPortfolio.rejected, setRejected)
            .addCase(createMyPortfolioItem.pending, setPending)
            .addCase(createMyPortfolioItem.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    state.portfolioItems = [action.payload, ...state.portfolioItems];
                }
            })
            .addCase(createMyPortfolioItem.rejected, setRejected)
            .addCase(updateMyPortfolioItem.pending, setPending)
            .addCase(updateMyPortfolioItem.fulfilled, (state, action) => {
                state.loading = false;
                state.portfolioItems = state.portfolioItems.map((item) => {
                    if (`${item.id}` === `${action.payload?.id}`) {
                        return action.payload;
                    }
                    return item;
                });
            })
            .addCase(updateMyPortfolioItem.rejected, setRejected)
            .addCase(deleteMyPortfolioItem.pending, setPending)
            .addCase(deleteMyPortfolioItem.fulfilled, (state, action) => {
                state.loading = false;
                state.portfolioItems = state.portfolioItems.filter((item) => `${item.id}` !== `${action.payload}`);
            })
            .addCase(deleteMyPortfolioItem.rejected, setRejected)
            .addCase(createMySkill.pending, setPending)
            .addCase(createMySkill.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    state.skills = [...state.skills, action.payload];
                }
            })
            .addCase(createMySkill.rejected, setRejected)
            .addCase(updateMySkill.pending, setPending)
            .addCase(updateMySkill.fulfilled, (state, action) => {
                state.loading = false;
                state.skills = state.skills.map((skill) => {
                    if (`${skill.id}` === `${action.payload?.id}`) {
                        return action.payload;
                    }
                    return skill;
                });
            })
            .addCase(updateMySkill.rejected, setRejected)
            .addCase(deleteMySkill.pending, setPending)
            .addCase(deleteMySkill.fulfilled, (state, action) => {
                state.loading = false;
                state.skills = state.skills.filter((skill) => `${skill.id}` !== `${action.payload}`);
            })
            .addCase(deleteMySkill.rejected, setRejected);
    }
});

export const {clearProfileState} = profileSlice.actions;
export default profileSlice.reducer;
